import { PropsWithChildren } from 'react';
import { makeStyles } from '@material-ui/core';
import HomeIcon from '@material-ui/icons/Home';
import ExtensionIcon from '@material-ui/icons/Extension';
import LibraryBooks from '@material-ui/icons/LibraryBooks';
import MemoryIcon from '@material-ui/icons/Memory';
import CategoryIcon from '@material-ui/icons/Category';
import CreateComponentIcon from '@material-ui/icons/AddCircleOutline';
import DnsIcon from '@material-ui/icons/Dns';
import ComputerIcon from '@material-ui/icons/Computer';
import SettingsEthernetIcon from '@material-ui/icons/SettingsEthernet';
import FolderSpecialIcon from '@material-ui/icons/FolderSpecial';
import AlbumIcon from '@material-ui/icons/Album';
import {
  BrokerIcon,
  DatabaseIcon,
  AirflowIcon,
  DefectDojoIcon,
  FeastIcon,
  JupyterIcon,
  KubernetesIcon,
  MlModelIcon,
  RustfsIcon,
} from '../icons';
import { configApiRef, useApi } from '@backstage/core-plugin-api';
import {
  CLOUDRU_SERVICES,
  isCloudruEnabled,
  isProxmoxEnabled,
  isVkcloudEnabled,
  ManagedService,
  routes,
  VKCLOUD_SERVICES,
} from '../../lib/viva';
import LogoFull from './LogoFull';
import LogoIcon from './LogoIcon';
import {
  Settings as SidebarSettings,
  UserSettingsSignInAvatar,
} from '@backstage/plugin-user-settings';
import { SidebarSearchModal } from '@backstage/plugin-search';
import {
  Sidebar,
  sidebarConfig,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarPage,
  SidebarScrollWrapper,
  SidebarSpace,
  useSidebarOpenState,
  Link,
} from '@backstage/core-components';
import MenuIcon from '@material-ui/icons/Menu';
import SearchIcon from '@material-ui/icons/Search';
import { MyGroupsSidebarItem } from '@backstage/plugin-org';
import {
  GitlabIcon,
  KafkaIcon,
  MongodbIcon,
  PostgresIcon,
  RabbitmqIcon,
  ServiceIcon,
  ValkeyIcon,
  CloudIcon,
} from '../icons';
import AllInclusiveIcon from '@material-ui/icons/AllInclusive';
import SecurityIcon from '@material-ui/icons/Security';
import { SidebarSection } from './SidebarSection';
import GroupIcon from '@material-ui/icons/People';
import { NotificationsSidebarItem } from '@backstage/plugin-notifications';

const useSidebarLogoStyles = makeStyles({
  root: {
    width: sidebarConfig.drawerWidthClosed,
    height: 3 * sidebarConfig.logoHeight,
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
    marginBottom: -14,
  },
  link: {
    width: sidebarConfig.drawerWidthClosed,
    marginLeft: 24,
  },
});

const SidebarLogo = () => {
  const classes = useSidebarLogoStyles();
  const { isOpen } = useSidebarOpenState();

  return (
    <div className={classes.root}>
      <Link to="/" underline="none" className={classes.link} aria-label="Home">
        {isOpen ? <LogoFull /> : <LogoIcon />}
      </Link>
    </div>
  );
};

// знак технологии пунктов разделов облаков (MySQL — облако)
const MANAGED_ICONS: Record<string, typeof CloudIcon> = {
  postgres: PostgresIcon,
  redis: ValkeyIcon,
  rabbitmq: RabbitmqIcon,
};

/** Раздел меню облака: пункт на каждый managed-сервис. */
const ManagedCloudSection = ({
  id,
  text,
  services,
}: {
  id: string;
  text: string;
  services: ManagedService[];
}) => (
  <SidebarSection
    id={id}
    icon={CloudIcon}
    text={text}
    paths={services.map(s => s.path)}
  >
    {services.map(s => (
      <SidebarItem
        key={s.kind}
        icon={MANAGED_ICONS[s.kind] ?? CloudIcon}
        to={s.path}
        text={s.menu}
      />
    ))}
  </SidebarSection>
);

export const Root = ({ children }: PropsWithChildren<{}>) => {
  // разделы «cloud.ru» и «VK Cloud» — только на стенде с учётными данными платформы облака
  // (viva.cloudru.enabled / viva.vkcloud.enabled)
  const config = useApi(configApiRef);
  const cloudru = isCloudruEnabled(config);
  const vkcloud = isVkcloudEnabled(config);
  // раздел «Proxmox» — только на стенде с площадками Proxmox VE (viva.proxmox.enabled)
  const proxmox = isProxmoxEnabled(config);
  return (
    <SidebarPage>
      <Sidebar>
        <SidebarLogo />
        <SidebarGroup label="Поиск" icon={<SearchIcon />} to="/search">
          <SidebarSearchModal />
        </SidebarGroup>
        <SidebarDivider />
        <SidebarGroup label="Меню" icon={<MenuIcon />}>
          {/* Главная — сводка по всем ресурсам; дальше разделы по направлениям (раскрываемые) */}
          <SidebarItem icon={HomeIcon} to={routes.home} text="Главная" />
          <SidebarSection
            id="devops"
            icon={AllInclusiveIcon}
            text="DevOps"
            paths={[
              routes.clusters,
              routes.gitlab,
              routes.services,
              routes.rustfs,
            ]}
          >
            <SidebarItem
              icon={KubernetesIcon}
              to={routes.clusters}
              text="Кластеры"
            />
            {/* GitLab платформы (бутстрап-нода): версия, обновления, метрики */}
            <SidebarItem icon={GitlabIcon} to={routes.gitlab} text="GitLab" />
            <SidebarItem
              icon={ServiceIcon}
              to={routes.services}
              text="Сервисы"
            />
            {/* RustFS — S3 кластера на хостах (только просмотр, ставится вместе с кластером) */}
            <SidebarItem icon={RustfsIcon} to={routes.rustfs} text="RustFS" />
          </SidebarSection>
          <SidebarSection
            id="mlops"
            icon={MlModelIcon}
            text="MLOps"
            paths={[
              routes.models,
              routes.jupyterhub,
              routes.feast,
              routes.airflow,
            ]}
          >
            <SidebarItem icon={MlModelIcon} to={routes.models} text="Модели" />
            <SidebarItem
              icon={JupyterIcon}
              to={routes.jupyterhub}
              text="JupyterHub"
            />
            <SidebarItem icon={FeastIcon} to={routes.feast} text="Feast" />
            <SidebarItem
              icon={AirflowIcon}
              to={routes.airflow}
              text="Airflow"
            />
          </SidebarSection>
          <SidebarSection
            id="databases"
            icon={DatabaseIcon}
            text="Базы данных"
            paths={[routes.databases, routes.mongodb, routes.valkey]}
          >
            <SidebarItem
              icon={PostgresIcon}
              to={routes.databases}
              text="PostgreSQL"
            />
            {/* хранилища на операторах (компоненты кластера mongodb, rabbitmq, valkey) */}
            <SidebarItem
              icon={MongodbIcon}
              to={routes.mongodb}
              text="MongoDB"
            />
            <SidebarItem icon={ValkeyIcon} to={routes.valkey} text="Valkey" />
          </SidebarSection>
          <SidebarSection
            id="brokers"
            icon={BrokerIcon}
            text="Брокеры сообщений"
            paths={[routes.brokers, routes.rabbitmq]}
          >
            <SidebarItem icon={KafkaIcon} to={routes.brokers} text="Kafka" />
            <SidebarItem
              icon={RabbitmqIcon}
              to={routes.rabbitmq}
              text="RabbitMQ"
            />
          </SidebarSection>
          {/* managed-сервисы cloud.ru Advanced (RDS, DCS, DMS) — только на стенде с ключом платформы */}
          {cloudru && (
            <ManagedCloudSection
              id="cloudru"
              text="cloud.ru"
              services={CLOUDRU_SERVICES}
            />
          )}
          {/* managed-базы VK Cloud (Cloud Databases) — только на стенде с пользователем платформы VK */}
          {vkcloud && (
            <ManagedCloudSection
              id="vkcloud"
              text="VK Cloud"
              services={VKCLOUD_SERVICES}
            />
          )}
          {/* площадки Proxmox VE: живые данные их API (viva-proxmox), ресурсы — шаблонами proxmox-* */}
          {proxmox && (
            <SidebarSection
              id="proxmox"
              icon={DnsIcon}
              text="Proxmox"
              paths={[routes.proxmox]}
            >
              <SidebarItem icon={DnsIcon} to={`${routes.proxmox}/sites`} text="Площадки" />
              <SidebarItem icon={ComputerIcon} to={`${routes.proxmox}/vms`} text="Виртуальные машины" />
              <SidebarItem icon={SettingsEthernetIcon} to={`${routes.proxmox}/networks`} text="Сети" />
              <SidebarItem icon={FolderSpecialIcon} to={`${routes.proxmox}/pools`} text="Пулы" />
              <SidebarItem icon={AlbumIcon} to={`${routes.proxmox}/images`} text="Образы" />
            </SidebarSection>
          )}
          <SidebarSection
            id="security"
            icon={SecurityIcon}
            text="Security"
            paths={[routes.defectdojo]}
          >
            <SidebarItem
              icon={DefectDojoIcon}
              to={routes.defectdojo}
              text="DefectDojo"
            />
          </SidebarSection>
          <SidebarItem
            icon={CreateComponentIcon}
            to="create"
            text="Создать..."
          />
          <SidebarDivider />
          {/* Общая навигация */}
          <SidebarItem icon={CategoryIcon} to="catalog" text="Каталог" />
          <MyGroupsSidebarItem
            singularTitle="Моя группа"
            pluralTitle="Мои группы"
            icon={GroupIcon}
          />
          <SidebarItem icon={ExtensionIcon} to="api-docs" text="API" />
          <SidebarItem icon={LibraryBooks} to="docs" text="Документация" />
          <SidebarItem icon={MemoryIcon} to="ai-access" text="ИИ-агенты" />
          <SidebarScrollWrapper>
            {/* Items in this group will be scrollable if they run out of space */}
          </SidebarScrollWrapper>
        </SidebarGroup>
        <SidebarSpace />
        <SidebarDivider />
        <NotificationsSidebarItem />
        <SidebarDivider />
        <SidebarGroup
          label="Настройки"
          icon={<UserSettingsSignInAvatar />}
          to="/settings"
        >
          <SidebarSettings />
        </SidebarGroup>
      </Sidebar>
      {children}
    </SidebarPage>
  );
};
